/**
 * Krio Phrase Dictionary
 *
 * Maps common Krio greetings and reunion-related phrases to instant responses.
 * Matching is case-insensitive and supports partial matches for common variations.
 */

interface KrioPhrase {
  /** Krio phrase patterns (lowercase). Supports multiple variations. */
  patterns: string[];
  /** Response in both Krio and English */
  response: string;
}

const KRIO_PHRASES: KrioPhrase[] = [
  // ── Greetings ──────────────────────────────────────────────────
  {
    patterns: ['kusheh', 'kusheh-o', 'kushe'],
    response: 'Kusheh-o! 🙌 Welcome to the KDU Global Reunion. How can I help you today? (Wetin a kin ep yu wit?)',
  },
  {
    patterns: ['aw di bodi', 'aw de bodi', 'how di body', 'ow di bodi'],
    response: 'A de fine tenki! 😊 The body is well. Welcome to the reunion hub. Ask me anything about the weekend events!',
  },
  {
    patterns: ['aw yu de', 'ow yu de', 'how you dey'],
    response: 'A de ya-o! Fine fine. 🎉 Ready to help you with reunion info. Wetin yu wan know?',
  },
  {
    patterns: ['tenki', 'tankyu', 'tank yu', 'tenk yu'],
    response: 'Yu welcome-o! 🙏 Glad I could help. If you need anything else about the reunion, just ask!',
  },
  {
    patterns: ['a de go', 'a de go naw', 'leh wi tok back'],
    response: 'Aight-o! 👋 Safe journey. See you at the reunion! (Wi go si back!)',
  },
  {
    patterns: ['paddy', 'padi', 'mi paddy'],
    response: 'Yes paddy! 🤝 Your KDU family is here. What can I help you with for the reunion weekend?',
  },
  {
    patterns: ['wetin na dis', 'wetin bi dis'],
    response: 'Dis na the KDU Georgia 2026 Reunion Hub! 🌍 It helps you find event info, register, connect with subcommittees, and stay updated on everything happening during the reunion weekend.',
  },

  // ── Reunion-Related ────────────────────────────────────────────
  {
    patterns: ['wetin de happen', 'wetin de go on', 'wetin dey happen'],
    response: 'Plenty things de happen for the reunion! 🎊 Check the Events & Schedule page for the full lineup — Legacy Ball, Awujoh, Meet & Greet, Symposium, and more. Ask me about any specific event!',
  },
  {
    patterns: ['usai di ball de', 'usai di legacy ball', 'usai di ball'],
    response: 'The Legacy Ball is at the Cobb Galleria Centre in Atlanta, Georgia! 🎭 Check the Events page for the exact date and time. Formal attire required — dress to impress!',
  },
  {
    patterns: ['usai di awujoh', 'usai di awujoh de'],
    response: 'The Awujoh (traditional feast) venue details are on the Events page! 🍲 It is one of the cultural highlights — traditional Krio cuisine, music, and fellowship. Don\'t miss it!',
  },
  {
    patterns: ['aw a kin register', 'aw fo register', 'a wan register'],
    response: 'To register, go to the Registration page in the sidebar! 🎫 Pick your ticket tier, fill in your details, and you\'re set. Early registration is recommended — some events have limited spots.',
  },
  {
    patterns: ['wetin na di program', 'wetin na di schedule', 'wetin de for weekend'],
    response: 'The reunion weekend is packed! 📅 Events include: Meet & Greet, Legacy Ball, Awujoh, Symposium, Young Adult Night, Picnic, Tata & Panlamp Ceremony, and Thanksgiving services. Check Events & Schedule for times and venues!',
  },
  {
    patterns: ['wi go si', 'wi go si back', 'si yu'],
    response: 'Wi go si-o! 🌟 See you at the reunion! Don\'t forget to check your My Weekend page for your personalized schedule.',
  },
  {
    patterns: ['na we yone dis', 'na wi yone', 'krio to di bone'],
    response: 'Na so! 🇸🇱 This is our heritage, our reunion, our celebration. KDU family strong! Welcome home to Georgia 2026.',
  },
  {
    patterns: ['aw much di ticket', 'aw much fo pay', 'wetin di price'],
    response: 'Ticket prices vary by tier — check the Registration page for current pricing! 💰 Different tiers include different event combinations. Early bird rates may be available.',
  },
  {
    patterns: ['usai a kin sleep', 'usai di hotel', 'usai fo stay'],
    response: 'Hotel room blocks are arranged near the event venues with group discounts! 🏨 Check the Accommodations section for hotel names, rates, and booking links. Book early — rooms fill up fast!',
  },
];

/**
 * Check if a user message matches a known Krio phrase.
 * Returns the response string if matched, null otherwise.
 * Matching is case-insensitive and uses includes() for flexibility.
 */
export function matchKrioPhrase(input: string): string | null {
  const normalized = input.toLowerCase().trim();

  // Exact match first (most reliable)
  for (const phrase of KRIO_PHRASES) {
    for (const pattern of phrase.patterns) {
      if (normalized === pattern) {
        return phrase.response;
      }
    }
  }

  // Partial match (input contains the pattern or pattern contains the input)
  for (const phrase of KRIO_PHRASES) {
    for (const pattern of phrase.patterns) {
      if (normalized.includes(pattern) || pattern.includes(normalized)) {
        // Only match if the input is short enough to be a phrase (not a long sentence that happens to contain a Krio word)
        if (normalized.length <= pattern.length + 10) {
          return phrase.response;
        }
      }
    }
  }

  return null;
}
